import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tendApi, Tendv2ContentItem } from '../api/tendApi';
import { brandApi } from '../api/brandApi';
import { Logo, mapDefaultLogo } from '../api/logoApi';
import TendV2ContentCard from '../components/tend/TendV2ContentCard';

interface TendPostPageParams extends Record<string, string | undefined> {
  slug: string;
  contentId: string;
  itemId: string;
}

interface ContentMeta {
  title?: string;
  brandId?: number;
  brandName?: string;
  brandDefaultLogo?: Logo | null;
  brandLogoDisplayMode?: 'icon_only' | 'name_only' | 'icon_and_name';
}

const TendPostPage: React.FC = () => {
  const { slug, contentId: contentIdParam, itemId: itemIdParam } = useParams<TendPostPageParams>();
  const contentId = contentIdParam ? parseInt(contentIdParam, 10) : 0;
  const itemId = itemIdParam ? parseInt(itemIdParam, 10) : 0;

  const [item, setItem] = useState<Tendv2ContentItem | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contentMeta, setContentMeta] = useState<ContentMeta>({});
  const [curatorBrandId, setCuratorBrandId] = useState<number | undefined>(undefined);
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  useEffect(() => {
    if (!slug || !contentId || !itemId) return;

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const [brandData, tendItems] = await Promise.all([
          brandApi.getBrand(slug).catch(() => null),
          tendApi.getTendV2ContentItems(contentId),
        ]);

        if (cancelled) return;

        const found = tendItems.find((i) => i.id === itemId) || null;

        // Derive brand fields from brand-by-slug (reliable).
        const brandIdFromSlug = brandData?.brand_id ?? brandData?.id;
        const resolvedBrandId = brandIdFromSlug ? Number(brandIdFromSlug) : undefined;
        const resolvedDefaultLogo: Logo | null = mapDefaultLogo(brandData?.default_logo);

        setContentMeta({
          title: found?.source_title,
          brandId: resolvedBrandId,
          brandName: brandData?.name,
          brandDefaultLogo: resolvedDefaultLogo,
          brandLogoDisplayMode: (brandData?.logo_display_mode as any) || 'icon_and_name',
        });
        if (brandIdFromSlug) {
          setCuratorBrandId(Number(brandIdFromSlug));
        }

        if (!found) {
          setNotFound(true);
        } else {
          setItem(found);
        }
      } catch (err: any) {
        if (cancelled) return;
        console.error('[TendPostPage] Error loading:', err);
        setError(err?.message || 'Failed to load post');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [slug, contentId, itemId]);

  const handleCopyLink = useCallback(async () => {
    const url = `${window.location.origin}/${slug}/tend/${contentId}/item/${itemId}`;
    try {
      await navigator.clipboard.writeText(url);
      setIsLinkCopied(true);
      setTimeout(() => setIsLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy share link:', err);
    }
  }, [slug, contentId, itemId]);

  if (!slug || !contentId || !itemId) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p>Invalid URL.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4 min-w-0">
            <Link
              to={`/${slug}/tend/${contentId}`}
              className="text-gray-400 hover:text-white text-sm flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to all posts
            </Link>
            {contentMeta.title && (
              <h1 className="text-base font-medium text-gray-300 truncate">
                {contentMeta.title}
              </h1>
            )}
          </div>
          <button
            onClick={handleCopyLink}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded transition-colors"
            title="Copy share link to this post"
          >
            {isLinkCopied ? (
              <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 015.656 5.656l-3 3a4 4 0 01-5.656-5.656M10.172 13.828a4 4 0 01-5.656-5.656l3-3a4 4 0 015.656 5.656" />
              </svg>
            )}
          </button>
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="bg-white rounded-xl p-12 text-center text-gray-500">
            <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-green-600 rounded-full mx-auto mb-4"></div>
            Loading post...
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl p-12 text-center text-red-600">
            <p className="font-semibold mb-2">Couldn't load post</p>
            <p className="text-sm">{error}</p>
          </div>
        ) : notFound ? (
          <div className="bg-white rounded-xl p-12 text-center text-gray-700">
            <h2 className="text-xl font-semibold mb-2">Post not found</h2>
            <p className="text-sm text-gray-500 mb-6">
              This post may have been deleted or moved.
            </p>
            <Link
              to={`/${slug}/tend/${contentId}`}
              className="inline-block px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm"
            >
              View all posts for this article
            </Link>
          </div>
        ) : item ? (
          <div className="bg-white rounded-xl p-6">
            <div className="max-w-[420px] mx-auto">
              <TendV2ContentCard
                item={item}
                brandId={contentMeta.brandId}
                brandName={contentMeta.brandName}
                brandSlug={slug}
                contentId={contentId}
                contentTitle={contentMeta.title}
                brandDefaultLogo={contentMeta.brandDefaultLogo}
                brandLogoDisplayMode={contentMeta.brandLogoDisplayMode}
                curatorBrandId={curatorBrandId}
              />
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl p-12 text-center text-gray-500">
            <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-green-600 rounded-full mx-auto mb-4"></div>
            Loading post...
          </div>
        )}
      </div>
    </div>
  );
};

export default TendPostPage;
